/**
 * The collapsed rail's database picker (extracted from `CollapsedSidebar` so
 * that file stays readable, and so this surface can be unit-tested on its own).
 *
 * It used to derive its list by SCRAPING the Object Explorer tree for
 * `type === 'database'` children of the connection's databases-folder. Two bugs
 * came with that:
 *
 *  - it never descended into the `system-databases-folder`, so system databases
 *    were missing from the rail even though every other picker offers them;
 *  - a smart-fallback node (`metadata.unverified`, drawn when the listing
 *    FAILED) is an ordinary `'database'` node in the tree, so the rail happily
 *    presented guesses as though the server had listed them.
 *
 * It now reads the same shared per-connection listing every other picker uses
 * (`useDatabaseList`), gated on the menu's own open state so collapsing the
 * sidebar does not fire a server query on its own. On a failed / empty listing
 * it shows the shared explainer + *Show details* affordance over the
 * config/known entries, each marked unverified — the same presentation as the
 * Schema Overview dropdown (this is a `DropdownMenu`, not a `Select`, so
 * `FallbackSelectContent` — which emits `SelectItem`s — cannot be reused).
 */

import { useState } from 'react';
import { Check, Database } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useDatabaseList } from '../../features/database-objects/hooks/useDatabaseList';
import { DatabaseListingFallbackBanner } from '../../features/connection-diagnostics/components/DatabaseListingFallbackBanner';
import { UnverifiedDatabaseIndicator } from '../../features/connection-diagnostics/components/UnverifiedDatabaseIndicator';
import { useFallbackPresentation } from '../../features/connection-diagnostics/hooks/useFallbackPresentation';
import { CollapsedSidebarRailButton } from './CollapsedSidebarRailButton';
import { cn } from '../../lib/utils';

type CollapsedSidebarDatabaseMenuProps = {
  connectionId: string | undefined;
  selectedDatabase: string | undefined;
  onSelectDatabase: (database: string) => void;
};

export function CollapsedSidebarDatabaseMenu({
  connectionId,
  selectedDatabase,
  onSelectDatabase,
}: CollapsedSidebarDatabaseMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    databases: availableDatabases,
    state: databasesState,
    isInitialLoading: isDatabasesLoading,
  } = useDatabaseList({ connectionId, enabled: isOpen && !!connectionId });

  const fallback = useFallbackPresentation({ connectionId, state: databasesState });
  const listingFailed = databasesState === 'error' || databasesState === 'empty';

  const handleSelect = (database: string) => {
    onSelectDatabase(database);
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <CollapsedSidebarRailButton
              icon={
                isDatabasesLoading ? (
                  <RandomDotmSpinner size={14} className="text-muted-foreground" />
                ) : (
                  <Database
                    className={cn(
                      'h-3.5 w-3.5',
                      selectedDatabase ? 'text-muted-foreground' : 'text-muted-foreground/50'
                    )}
                  />
                )
              }
              className="mt-2 border-t border-border pt-2"
              aria-label={
                isDatabasesLoading
                  ? 'Loading databases'
                  : selectedDatabase
                    ? `Database: ${selectedDatabase}. Click to change database.`
                    : 'No database selected. Click to choose one.'
              }
              data-testid="rail-database-button"
            />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          {isDatabasesLoading ? 'Loading databases...' : (selectedDatabase || 'No database selected')}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="right" align="start" className="w-48 max-h-64 overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Databases</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isDatabasesLoading ? (
          <div
            data-testid="rail-database-loading"
            className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2"
          >
            <RandomDotmSpinner size={12} />
            Loading databases...
          </div>
        ) : listingFailed ? (
          <div className="flex flex-col gap-2 p-2" data-testid="rail-database-listing-failed">
            {connectionId ? (
              <DatabaseListingFallbackBanner
                connectionId={connectionId}
                variant="subline"
                trigger={databasesState === 'empty' ? 'list_empty' : 'list_failure'}
                beforeOpenDetails={() => setIsOpen(false)}
                data-testid="rail-database-fallback-banner"
                showDetailsTestId="rail-database-show-details"
                sendTestId="rail-database-send-diagnostic-report"
              />
            ) : null}
            {fallback.entries.length > 0 && (
              <div className="flex flex-col" data-testid="rail-database-fallback-entries">
                {fallback.entries.map((db) => (
                  <DropdownMenuItem
                    key={db}
                    onClick={() => handleSelect(db)}
                    className="flex items-center gap-2"
                  >
                    <Database className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="flex-1 truncate">{db}</span>
                    <UnverifiedDatabaseIndicator
                      kind={fallback.isConfig(db) ? 'config' : 'known'}
                      density="compact"
                      data-testid={`rail-database-fallback-indicator-${db}`}
                    />
                  </DropdownMenuItem>
                ))}
              </div>
            )}
          </div>
        ) : availableDatabases.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No databases loaded</div>
        ) : (
          availableDatabases.map((db) => (
            <DropdownMenuItem
              key={db}
              onClick={() => handleSelect(db)}
              className="flex items-center gap-2"
              data-testid={`rail-database-option-${db}`}
            >
              <Database className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1 truncate">{db}</span>
              {db === selectedDatabase && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
